/**
 * Aceite do boleto único pelo Portal do Consumidor (tarefa #5575) — regras puras.
 *
 * O Portal precisa responder três perguntas, e todas moram aqui para a tela, o modal e o
 * cartão do Início lerem a mesma resposta:
 *   1. Em que situação está cada UC do cliente — pode aderir, já aceitou, já está no boleto
 *      único, ou está fora (UC de parceira ou em situação que não adere)?
 *   2. Este cliente vê o convite? Só se tiver ao menos uma UC da Vigor que ainda pode aderir
 *      ou que já aceitou. Um cliente da Gran não recebe convite para o produto da Vigor.
 *   3. O modal abre sozinho? Só na PRIMEIRA vez. Depois disso o convite fica no cartão do
 *      Início — é aviso, não muro (texto da tarefa).
 *
 * A gravação não é feita aqui nem pelo navegador: é da função `aceitar_boleto_unico_portal`
 * (migration 20260914150000), que confere tudo de novo no servidor e preenche os campos.
 * O que esta lib decide é só o que MOSTRAR.
 */

import {
  classificarUc,
  indexarFaturasPorUc,
  janelaMeses,
  ucElegivel,
  type FaturaDebitoAutomatico,
  type StatusDebitoAutomatico,
} from './debitoAutomaticoEquatorial.ts';

export type SituacaoUcPortal =
  /** Da Vigor, elegível, sem boleto único e sem aceite: pode aceitar agora. */
  | 'pode_aderir'
  /** Já aceitou; a Vigor ainda não liberou. */
  | 'aceito_aguardando'
  /** Já cobrando Vigor + Equatorial num boleto só. */
  | 'ativo'
  /** Parceira ou situação que não adere. Não aparece no convite. */
  | 'fora';

/** Só o que a decisão lê da UC — tudo visível ao consumidor pela RLS dele. */
export interface UcPortalAceite {
  id: number;
  numero_uc?: string | null;
  outro_numero_uc?: string | null;
  situacao?: string | null;
  empresa_parceira_id?: string | null;
  boleto_unico?: boolean | null;
  boleto_unico_aceite_em?: string | null;
  endereco?: string | null;
  logradouro?: string | null;
  numero?: string | null;
  bairro?: string | null;
  cidade?: string | null;
}

export function situacaoUcNoPortal(uc: UcPortalAceite): SituacaoUcPortal {
  // Parceira primeiro: as UCs da Gran já estão no boleto único DELAS e não podem cair em
  // "ativo" aqui, senão o cliente leria que aderiu ao boleto da Vigor.
  if (uc.empresa_parceira_id) return 'fora';
  if (!ucElegivel(uc)) return 'fora';
  if (uc.boleto_unico === true) return 'ativo';
  if (uc.boleto_unico_aceite_em) return 'aceito_aguardando';
  return 'pode_aderir';
}

export interface LinhaUcPortal {
  uc: UcPortalAceite;
  situacao: SituacaoUcPortal;
  /**
   * `true` quando a fatura mais recente lida acusa débito automático na Equatorial. A tela
   * AVISA, não bloqueia: quem barra a liberação é a trava do banco, e a leitura vem de PDF.
   */
  debitoAutomatico: boolean;
}

export interface ConvitePortal {
  /** Só as UCs que interessam ao convite (sem as 'fora'), na ordem do cadastro. */
  linhas: LinhaUcPortal[];
  podeAderir: number;
  aguardando: number;
  ativas: number;
  /** O cliente vê o cartão do Início? */
  mostrarConvite: boolean;
}

export interface OpcoesConvite {
  hoje: Date;
  /** Mesma janela do painel e da #5570. */
  meses?: number;
}

export function montarConvitePortal(
  ucs: UcPortalAceite[],
  faturas: FaturaDebitoAutomatico[],
  opcoes: OpcoesConvite,
): ConvitePortal {
  const janela = janelaMeses(opcoes.hoje, opcoes.meses ?? 3);
  const porUc = indexarFaturasPorUc(faturas);

  const linhas: LinhaUcPortal[] = [];
  for (const uc of ucs) {
    const situacao = situacaoUcNoPortal(uc);
    if (situacao === 'fora') continue;
    const status: StatusDebitoAutomatico = classificarUc(uc, porUc, janela).status;
    linhas.push({ uc, situacao, debitoAutomatico: status === 'confirmado_sim' });
  }

  const conta = (s: SituacaoUcPortal) => linhas.filter(l => l.situacao === s).length;
  const podeAderir = conta('pode_aderir');
  const aguardando = conta('aceito_aguardando');
  const ativas = conta('ativo');

  return {
    linhas,
    podeAderir,
    aguardando,
    ativas,
    // Quem já tem tudo ativo também vê: o cartão confirma "está ativo" e evita a pergunta
    // "eu aderi?". Quem só tem UC 'fora' não vê nada.
    mostrarConvite: linhas.length > 0,
  };
}

/**
 * O modal abre sozinho? Só se há convite publicado, o cliente nunca o viu e ainda tem UC
 * para aderir. Uma vez visto, o convite não interrompe de novo — fica no cartão do Início.
 */
export function deveAbrirConviteAutomatico(params: {
  avisoPublicado: boolean;
  jaVisualizado: boolean;
  podeAderir: number;
}): boolean {
  return params.avisoPublicado && !params.jaVisualizado && params.podeAderir > 0;
}

/**
 * Seleção inicial do modal. Com uma UC só, ela já vem marcada — o cliente ainda precisa
 * rolar o texto e marcar "Li e concordo". Com várias, nenhuma vem marcada: cada unidade é
 * uma adesão, e marcar por ele seria decidir por ele.
 */
export function selecaoInicial(linhas: LinhaUcPortal[]): number[] {
  const aderiveis = linhas.filter(l => l.situacao === 'pode_aderir');
  return aderiveis.length === 1 ? [aderiveis[0].uc.id] : [];
}

export function textoBotaoAceite(quantidade: number): string {
  if (quantidade <= 0) return 'Escolha uma unidade';
  return quantidade === 1 ? 'Aceitar para 1 unidade' : `Aceitar para ${quantidade} unidades`;
}

export const ROTULOS_SITUACAO_PORTAL: Record<Exclude<SituacaoUcPortal, 'fora'>, { texto: string; ajuda: string }> = {
  pode_aderir: {
    texto: 'Pode aderir',
    ajuda: 'Você ainda paga separado. Se aceitar, passa a pagar Vigor e Equatorial num boleto só a partir de outubro de 2026.',
  },
  aceito_aguardando: {
    texto: 'Aceite registrado',
    ajuda: 'A Vigor está conferindo a unidade. Você recebe um WhatsApp quando ela estiver liberada; até lá, nada muda na sua cobrança.',
  },
  ativo: {
    texto: 'Boleto único ativo',
    ajuda: 'Você paga Vigor e Equatorial num boleto só. Não pague a fatura da Equatorial separadamente.',
  },
};

/** Motivos que a função devolve para UC que não entrou, em português de tela. */
export const MOTIVOS_ACEITE_RECUSADO: Record<string, string> = {
  nao_encontrada: 'unidade não encontrada no seu cadastro',
  parceira: 'unidade atendida por empresa parceira',
  nao_elegivel: 'unidade que não pode aderir no momento',
  ja_no_boleto_unico: 'unidade que já está no boleto único',
  ja_aceitou: 'unidade que já tinha aceite registrado',
};

/** "Rua X, 10 · Bairro" — o suficiente para o cliente reconhecer a unidade. */
export function enderecoCurtoUc(uc: UcPortalAceite): string {
  // Limpa antes de filtrar: cadastro com rua ou número só de espaço montava ", " e o cliente
  // via "," como endereço (achado do test-writer em 14/09/2026).
  const limpo = (v: unknown) => String(v ?? '').trim();
  const rua = [uc.logradouro, uc.numero].map(limpo).filter(Boolean).join(', ');
  const partes = [rua || limpo(uc.endereco), limpo(uc.bairro)].filter(Boolean);
  return partes.join(' · ');
}
